import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
	Input,
	InputError,
	InputLabel,
	InputWrapper,
} from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import {
	User,
	Phone,
	Mail,
	Image,
	CircleArrowLeft,
	Calendar,
	FileText,
} from 'lucide-react';
import { maskPhone, removeMask } from '@/utils/masks';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

const maskCpf = (cpf: string | null | undefined): string => {
	if (!cpf) return '';

	const cleanedCpf = cpf.replace(/\D/g, '');

	return cleanedCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const formSchema = z.object({
	first_name: z
		.string()
		.min(1, 'Nome é obrigatório')
		.min(3, 'Nome deve ter no mínimo 3 caracteres')
		.max(255, 'Nome deve ter no máximo 255 caracteres')
		.regex(
			/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/,
			'Apenas letras e acentos são permitidos',
		),
	last_name: z
		.string()
		.min(1, 'Sobrenome é obrigatório')
		.min(3, 'Sobrenome deve ter no mínimo 3 caracteres')
		.max(255, 'Sobrenome deve ter no máximo 255 caracteres')
		.regex(
			/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/,
			'Apenas letras e acentos são permitidos',
		)
		.optional(),
	phone_number: z
		.string()
		.min(1, 'Telefone é obrigatório')
		.transform(removeMask)
		.refine((val) => val.length === 11, 'Telefone deve conter 11 números')
		.refine((val) => /^\d{11}$/.test(val))
		.optional(),
	email: z.string().email(),
	profile_photo_url: z.string().optional().nullable(),
	birthday: z.string().optional(),

	cpf: z.string().nullable(),
});

type UserProfileFormValues = z.infer<typeof formSchema>;

const USER_READ_ONLY_DIRTY: ReadonlySet<keyof UserProfileFormValues> = new Set([
	'email',
	'cpf',
	'first_name',
	'last_name',
]);

const UserForm = ({
	defaultValues,
	isSubmitting,
	onSubmit,
}: {
	defaultValues?: Users.User;
	isSubmitting: boolean;
	onSubmit: (data: Partial<Users.Update>) => void;
}) => {
	const {
		register,
		handleSubmit,
		setValue,
		reset,
		formState: { errors, dirtyFields },
		watch,
	} = useForm<UserProfileFormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			first_name: defaultValues?.first_name ?? '',
			last_name: defaultValues?.last_name ?? '',
			phone_number: defaultValues?.phone_number ?? '',
			email: defaultValues?.email ?? '',
			birthday: defaultValues?.birthday
				? new Date(defaultValues.birthday).toISOString().split('T')[0]
				: '',
			profile_photo_url: defaultValues?.profile_photo_url ?? undefined,
			cpf: defaultValues?.cpf ?? null,
		},
	});

	const [showUpdateButton, setShowUpdateButton] = useState(false);

	useEffect(() => {
		const hasDirtyFields = Object.keys(dirtyFields).length > 0;
		setShowUpdateButton(hasDirtyFields);
	}, [dirtyFields]);

	useEffect(() => {
		if (!defaultValues) return;
		reset({
			first_name: defaultValues.first_name ?? '',
			last_name: defaultValues.last_name ?? '',
			phone_number: defaultValues.phone_number ?? '',
			email: defaultValues.email ?? '',
			birthday: defaultValues.birthday
				? new Date(defaultValues.birthday).toISOString().split('T')[0]
				: '',
			profile_photo_url: defaultValues.profile_photo_url ?? undefined,
			cpf: defaultValues.cpf ?? null,
		});
	}, [
		defaultValues?.id,
		defaultValues?.first_name,
		defaultValues?.last_name,
		defaultValues?.phone_number,
		defaultValues?.email,
		defaultValues?.birthday,
		defaultValues?.profile_photo_url,
		defaultValues?.cpf,
		reset,
	]);

	return (
		<div className="space-y-6 rounded-lg md:border md:p-4">
			<div className="flex items-center justify-between">
				<h3 className="text-md flex items-center gap-2 py-1 font-medium text-dark">
					Informações Pessoais
				</h3>
				{showUpdateButton && (
					<Button
						onClick={handleSubmit(async (data) => {
							const dirtyKeys = Object.keys(dirtyFields) as Array<
								keyof UserProfileFormValues
							>;
							const entries = dirtyKeys
								.filter(
									(k) =>
										dirtyFields[k] &&
										!USER_READ_ONLY_DIRTY.has(k),
								)
								.map((k) => [k, data[k]] as [string, unknown]);
							const patch = Object.fromEntries(
								entries,
							) as Partial<Users.Update>;
							if (Object.keys(patch).length === 0) return;
							await onSubmit(patch);
							reset({
								first_name: data.first_name,
								last_name: data.last_name,
								phone_number: data.phone_number,
								email: data.email,
								birthday: data.birthday,
								profile_photo_url: data.profile_photo_url,
								cpf: data.cpf,
							});
						})}
						disabled={isSubmitting}
						text="Atualizar Informações"
						className="text-sm"
					/>
				)}
			</div>

			<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div className="space-y-1">
					<InputLabel
						text="Nome"
						icon={<User className="h-4 w-4 text-dark" />}
					/>
					<InputWrapper>
						<Input
							readOnly
							placeholder="Ex: João"
							{...register('first_name')}
						/>
					</InputWrapper>
					{errors.first_name?.message && (
						<InputError
							error={errors.first_name.message as string}
						/>
					)}
				</div>

				<div className="space-y-1">
					<InputLabel
						text="Sobrenome"
						icon={<User className="h-4 w-4 text-dark" />}
					/>
					<InputWrapper>
						<Input
							readOnly
							placeholder="Ex: Silva"
							{...register('last_name')}
						/>
					</InputWrapper>
					{errors.last_name?.message && (
						<InputError
							error={errors.last_name.message as string}
						/>
					)}
				</div>
			</div>

			<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div className="space-y-1">
					<InputLabel
						text="Telefone"
						icon={<Phone className="h-4 w-4 text-dark" />}
					/>
					<InputWrapper>
						<Input
							value={maskPhone(watch('phone_number') || '')}
							{...register('phone_number')}
							onChange={(e) =>
								setValue(
									'phone_number',
									maskPhone(e.target.value),
									{
										shouldDirty: true,
									},
								)
							}
							placeholder="Ex: (11) 98765-4321"
							maxLength={15}
						/>
					</InputWrapper>
					{errors.phone_number?.message && (
						<InputError
							error={errors.phone_number.message as string}
						/>
					)}
				</div>
				<div className="space-y-1">
					<InputLabel
						text="Foto de perfil"
						icon={<Image className="h-4 w-4 text-dark" />}
					/>
					<Button
						type="button"
						variant="text"
						text="Alterar foto (em breve)"
						className="h-10 w-full justify-center rounded-md border border-dashed border-zinc-300 text-sm text-zinc-500"
						onClick={() =>
							toast.info(
								'Em breve você poderá alterar sua foto de perfil.',
							)
						}
					/>
				</div>
			</div>

			<div className="space-y-1">
				<InputLabel
					text="Email"
					icon={<Mail className="h-4 w-4 text-dark" />}
				/>
				<InputWrapper>
					<Input
						readOnly
						placeholder="Ex: silva@email.com"
						{...register('email')}
					/>
				</InputWrapper>
				{errors.email?.message && (
					<InputError error={errors.email.message as string} />
				)}
			</div>
			<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div className="space-y-1">
					<InputLabel
						text="Data de Nascimento"
						icon={<Calendar className="h-4 w-4 text-dark" />}
					/>
					<InputWrapper>
						<Input type="date" {...register('birthday')} />
					</InputWrapper>
					{errors.birthday?.message && (
						<InputError error={errors.birthday.message as string} />
					)}
				</div>
				<div className="space-y-1">
					<InputLabel
						text="CPF"
						icon={<FileText className="h-4 w-4 text-dark" />}
					/>
					<InputWrapper>
						<Input
							readOnly
							{...register('cpf')}
							value={maskCpf(watch('cpf'))}
							placeholder="000.000.000-00"
						/>
					</InputWrapper>
					{errors.cpf?.message && (
						<InputError error={errors.cpf.message as string} />
					)}
				</div>
			</div>
		</div>
	);
};

interface UserFormProps {
	onSubmit: (data: Partial<Users.Update>) => void;
	onBack: () => void;
	defaultValues?: Users.User;
	isSubmitting?: boolean;
	submitButtonText?: string;
}

const UserProfileForm = ({
	onSubmit,
	defaultValues,
	isSubmitting: externalIsSubmitting = false,
	onBack,
}: UserFormProps) => {
	return (
		<div className="max-w-2xl space-y-6 bg-inherit">
			<UserForm
				defaultValues={defaultValues}
				onSubmit={onSubmit}
				isSubmitting={externalIsSubmitting}
			/>
			<Button
				startIcon={<CircleArrowLeft className="size-4 text-dark" />}
				variant="text"
				text="voltar"
				className="w-fit justify-center text-base"
				onClick={onBack}
			/>
		</div>
	);
};

export { UserProfileForm };
